/**
 * GiftCatalogPanel - Catalogue de cadeaux virtuels
 * Permet d'envoyer des cadeaux contre des couronnes
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Input } from '../../ui/input';
import { api } from '../../../services/api';
import { toast } from 'sonner';

export function GiftCatalogPanel({ isDarkMode, customerId, targetId, targetName, onClose }) {
  const [catalog, setCatalog] = useState({ gifts: {}, conversions: {} });
  const [myCrowns, setMyCrowns] = useState(0);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  
  useEffect(() => {
    const fetchData = async () => {
      const [catRes, crownRes] = await Promise.all([
        api.getGiftsCatalog(),
        api.getCrowns(customerId)
      ]);
      setCatalog(catRes);
      setMyCrowns(crownRes.crowns || 0);
    };
    fetchData();
  }, [customerId]);
  
  const sendGift = async (giftType) => {
    if (sending) return;
    const gift = catalog.gifts[giftType];
    if (!gift) return;
    
    if (myCrowns < gift.price) {
      toast.error(`Pas assez de couronnes ! (${gift.price} 👑 requis)`);
      return;
    }
    
    setSending(true);
    try {
      await api.sendVirtualGift(customerId, targetId, giftType, message);
      toast.success(`${gift.emoji} Cadeau envoyé à ${targetName} !`);
      setMyCrowns(prev => prev - gift.price);
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur envoi cadeau');
    } finally {
      setSending(false);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          🎁 Envoyer un cadeau à {targetName}
        </h3>
        <div className="flex items-center gap-1 text-yellow-500 font-bold">
          👑 {myCrowns}
        </div>
      </div>
      
      <Input
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Message avec le cadeau (optionnel)"
        className={isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : ''}
      />
      
      <div className="grid grid-cols-4 gap-2">
        {Object.entries(catalog.gifts).map(([key, gift]) => (
          <motion.button
            key={key}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => sendGift(key)}
            disabled={sending || myCrowns < gift.price}
            className={`p-3 rounded-xl text-center transition-all ${
              myCrowns >= gift.price
                ? isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                : 'opacity-50 cursor-not-allowed'
            } ${isDarkMode ? '' : 'border border-gray-200'}`}
          >
            <div className="text-3xl mb-1">{gift.emoji}</div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{gift.name}</div>
            <div className="text-xs text-yellow-500 font-bold">{gift.price} 👑</div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

export default GiftCatalogPanel;
